import { memo, useMemo } from 'react';
import { SidebarBoxKey } from 'AdmissionsGuidance/StudentDashboard.types';
import { useTranslation } from 'react-i18next';
import { useNavigationHelper } from 'FrontRoyalAngular';
import { type ButtonConfig } from '../../StudentDashboardSidebar.types';
import { BasicDesktopBox } from '../../BasicDesktopBox';
import { type StatusConfig } from './AdmittedStudentsZoomBox.types';
import { useAdmittedStudentsZoomBoxConfig } from './useAdmittedStudentsZoomBoxConfig';
import { BasicMobileBox } from '../../BasicMobileBox';

function AdmittedStudentsZoomBoxComponent({ mobile }: { mobile?: boolean }) {
    const { zoomBoxStatusConfig } = useAdmittedStudentsZoomBoxConfig();
    const { t } = useTranslation('back_royal', { keyPrefix: 'admittedStudentsZoomBox' });
    const { loadUrl } = useNavigationHelper();

    const buttons = useMemo<ButtonConfig[]>(
        () =>
            zoomBoxStatusConfig.button?.key
                ? [
                      {
                          id: 'admitted-students-zoom-cta',
                          text: t(zoomBoxStatusConfig.button.key, zoomBoxStatusConfig.button.params),
                          onPress: () => loadUrl(zoomBoxStatusConfig.ctaUrl, '_blank'),
                      },
                  ]
                : [],
        [zoomBoxStatusConfig, loadUrl, t],
    );

    if (!zoomBoxStatusConfig?.heading || !zoomBoxStatusConfig?.text) {
        return null;
    }

    return mobile ? (
        <AdmittedStudentsZoomBoxMobilePresentation zoomBoxStatusConfig={zoomBoxStatusConfig} buttons={buttons} />
    ) : (
        <AdmittedStudentsZoomBoxPresentation zoomBoxStatusConfig={zoomBoxStatusConfig} buttons={buttons} />
    );
}

function AdmittedStudentsZoomBoxPresentation({
    zoomBoxStatusConfig,
    buttons,
}: {
    zoomBoxStatusConfig: StatusConfig;
    buttons: ButtonConfig[];
}) {
    const { t } = useTranslation('back_royal', { keyPrefix: 'admittedStudentsZoomBox' });
    return (
        <BasicDesktopBox
            boxKey={SidebarBoxKey.admitted_students_zoom}
            headerText={t(zoomBoxStatusConfig.heading!.key, zoomBoxStatusConfig.heading!.params)}
            buttons={buttons}
        >
            {t(zoomBoxStatusConfig.text!.key, zoomBoxStatusConfig.text!.params)}
        </BasicDesktopBox>
    );
}

function AdmittedStudentsZoomBoxMobilePresentation({
    zoomBoxStatusConfig,
    buttons,
}: {
    zoomBoxStatusConfig: StatusConfig;
    buttons: ButtonConfig[];
}) {
    const { t } = useTranslation('back_royal', { keyPrefix: 'admittedStudentsZoomBox' });
    return (
        <BasicMobileBox
            boxKey={SidebarBoxKey.admitted_students_zoom}
            heading={t(zoomBoxStatusConfig.heading!.key, zoomBoxStatusConfig.heading!.params)}
            ctaText={t('buttonMore')}
            expandedContent={
                <AdmittedStudentsZoomBoxPresentation zoomBoxStatusConfig={zoomBoxStatusConfig} buttons={buttons} />
            }
        />
    );
}

export const AdmittedStudentsZoomBox = memo(
    AdmittedStudentsZoomBoxComponent,
) as unknown as typeof AdmittedStudentsZoomBoxComponent;
